// FeedbackItem.js - Draggable feedback item for admin list
import React from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import THEME from '../../styles/theme';

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
  padding: ${THEME.spacing.md};
  background: ${props => props.$selected ? 'rgba(220, 20, 60, 0.1)' : THEME.inputBackground};
  border: 1px solid ${props => props.$selected ? THEME.accentPrimary : 'transparent'};
  border-radius: ${THEME.borderRadiusMedium};
  margin-bottom: ${THEME.spacing.sm};
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: ${props => props.$selected ? 'rgba(220, 20, 60, 0.15)' : 'rgba(255, 255, 255, 0.03)'};
  }
`;

const DragHandle = styled.div`
  cursor: grab;
  color: ${THEME.textMuted};
  padding: ${THEME.spacing.xs};
  display: flex;
  align-items: center;

  &:active {
    cursor: grabbing;
  }
`;

const TypeIcon = styled.span`
  font-size: ${THEME.fontSize.lg};
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  margin-top: ${THEME.spacing.xs};
`;

const MetaItem = styled.span`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.xs};
`;

const Username = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
`;

const StatusBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status) {
      case 'completed': return 'rgba(76, 175, 80, 0.2)';
      case 'in_progress': return 'rgba(33, 150, 243, 0.2)';
      case 'acknowledged': return 'rgba(156, 39, 176, 0.2)';
      case 'wont_fix': return 'rgba(158, 158, 158, 0.2)';
      default: return 'rgba(255, 193, 7, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'completed': return THEME.success;
      case 'in_progress': return THEME.info;
      case 'acknowledged': return '#9C27B0';
      case 'wont_fix': return THEME.textMuted;
      default: return '#FFC107';
    }
  }};
`;

const PriorityBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${props => {
    switch (props.$priority) {
      case 'high': return 'rgba(244, 67, 54, 0.2)';
      case 'low': return 'rgba(158, 158, 158, 0.2)';
      default: return 'rgba(255, 152, 0, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$priority) {
      case 'high': return '#F44336';
      case 'low': return THEME.textMuted;
      default: return '#FF9800';
    }
  }};
`;

const Badges = styled.div`
  display: flex;
  gap: ${THEME.spacing.xs};
  flex-shrink: 0;
`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatStatus = (status) => {
  const statusMap = {
    submitted: 'New',
    acknowledged: 'Ack',
    in_progress: 'In Progress',
    completed: 'Done',
    wont_fix: "Won't Fix"
  };
  return statusMap[status] || status;
};

const FeedbackItem = ({ item, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <ItemContainer
      ref={setNodeRef}
      style={style}
      $selected={isSelected}
      onClick={onSelect}
    >
      <DragHandle {...attributes} {...listeners}>
        ⋮⋮
      </DragHandle>

      <TypeIcon>
        {item.type === 'bug' ? '🐛' : '✨'}
      </TypeIcon>

      <ItemContent>
        <ItemTitle>{item.title}</ItemTitle>
        <ItemMeta>
          <Username>@{item.username}</Username>
          <MetaItem>{formatDate(item.created_at)}</MetaItem>
          {item.area && <MetaItem>{item.area}</MetaItem>}
        </ItemMeta>
      </ItemContent>

      <Badges>
        <PriorityBadge $priority={item.priority}>
          {item.priority}
        </PriorityBadge>
        <StatusBadge $status={item.status}>
          {formatStatus(item.status)}
        </StatusBadge>
      </Badges>
    </ItemContainer>
  );
};

export default FeedbackItem;
